import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  InputBase,
  Box,
  Typography,
  Button,
  Rating,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { setSearchKeys } from "../../lib/redux/slices/searchKeysSlice";
import { fetchWorker } from "../../lib/redux/slices/workerSlice";
import { t } from "i18next";

const SearchBar = () => {
  const searchBarRef = useRef(null);
  const dispatch = useDispatch();
  const [activeBox, setActiveBox] = useState(null);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const subServiceId = useSelector(
    (state) => state.activeSubServiceIdReducer.subServiceId
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target)
      ) {
        setActiveBox(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchWorker = (e) => {
    e.preventDefault();

    let lat = searchKeys?.workerLocation?.latitude || "";
    let lng = searchKeys?.workerLocation?.longitude || "";

    if (
      !searchKeys?.workerName &&
      !lat &&
      !lng &&
      searchKeys?.workerRating <= 0 &&
      searchKeys?.workerDateTime == null
    )
      return;

    const query = {
      serviceId: subServiceId,
      lat: lat,
      lng: lng,
      name: searchKeys?.workerName || "",
      rating: searchKeys?.workerRating || "",
      date: searchKeys?.workerDateTime == null,
    };

    dispatch(fetchWorker(query));
  };

  return (
    <Paper
      ref={searchBarRef}
      elevation={1}
      sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: 32,
        maxWidth: 550,
        width: "100%",
        mx: 2,
        border: "1px solid #DDDDDD",
        boxShadow: "0 3px 12px 0 rgba(0,0,0,0.1),0 1px 2px 0 rgba(0,0,0,0.08)",
        gap: 1,
        bgcolor: activeBox !== null ? "#EBEBEB" : "white",
        transition: "all 0.3s ease",
        "&:hover": { boxShadow: 2 },
        "@media (max-width: 600px)": {
          maxWidth: "50%",
          borderRadius: 25,
          gap: 0.5,
        },
      }}
    >
      <Box
        onClick={() => setActiveBox(0)}
        sx={{
          borderRadius: "32px",
          boxShadow: activeBox === 0 ? 4 : 0,
          flex: 1,
          paddingLeft: 4,
          py: 0.6,
          borderColor: "divider",
          transition: "all 0.3s ease",
          cursor: "pointer",
          bgcolor: "white",
          "&:hover": {
            bgcolor: activeBox === 0 ? "white" : "grey.100",
          },
          "&:not(:hover)": {
            bgcolor:
              activeBox !== null && activeBox !== 0 ? "#EBEBEB" : "white",
          },
          "@media (max-width: 600px)": {
            paddingLeft: 2,
            py: 0.4,
          },
        }}
      >
        <Box
          sx={{
            borderRight: activeBox === null ? "1px solid #EBEBEB" : "none",
            width: "90%",
            "&:hover": { borderRight: "none" },
            "@media (max-width: 600px)": {
              width: "100%",
            },
          }}
        >
          <Typography
            variant="caption"
            fontFamily="Poppins, sans-serif"
            fontWeight={600}
            lineHeight={0}
            sx={{
              "@media (max-width: 600px)": {
                fontSize: "0.65rem",
              },
            }}
          >
            {t("search")} {t("worker")}
          </Typography>
          <InputBase
            fullWidth
            type="text"
            placeholder={`${t("search")} ${t("worker")}`}
            value={searchKeys.workerName}
            onChange={(e) => {
              dispatch(
                setSearchKeys({ ...searchKeys, workerName: e.target.value })
              );
            }}
            sx={{
              fontSize: "0.875rem",
              "@media (max-width: 600px)": {
                fontSize: "0.65rem",
              },
            }}
          />
        </Box>
      </Box>
      <Box
        onClick={() => setActiveBox(1)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          borderRadius: "32px",
          boxShadow: activeBox === 1 ? 4 : 0,
          flex: 1,
          paddingLeft: 2,
          py: 1,
          paddingRight: "10px",
          borderColor: "divider",
          transition: "all 0.3s ease",
          cursor: "pointer",
          bgcolor: "white",
          "&:hover": {
            bgcolor: activeBox === 1 ? "white" : "grey.100",
          },
          "&:not(:hover)": {
            bgcolor:
              activeBox !== null && activeBox !== 1 ? "#EBEBEB" : "white",
          },
          "@media (max-width: 600px)": {
            px: 1,
            py: 0.8,
          },
        }}
      >
        <Box display="flex" flexDirection="column" marginTop="2px">
          <Typography
            variant="caption"
            fontFamily="Poppins, sans-serif"
            fontWeight={600}
            paddingLeft={2}
            sx={{
              "@media (max-width: 600px)": {
                fontSize: "0.65rem",
              },
            }}
          >
            {t("give")} {t("rating")}
          </Typography>
          <Rating
            name="worker-rating"
            precision={0.5}
            value={searchKeys.workerRating}
            onChange={(event, newValue) => {
              dispatch(
                setSearchKeys({ ...searchKeys, workerRating: newValue })
              );
            }}
            sx={{
              paddingLeft: 1,
              mt: 0.1,
              "& .MuiRating-icon": {
                fontSize: "1.3rem",
                color: "#FFB400",
              },
              "& .MuiRating-iconEmpty": {
                color: "#CBD5E1",
              },
              "@media (max-width: 600px)": {
                "& .MuiRating-icon": {
                  fontSize: "0.8715rem",
                },
              },
            }}
          />
        </Box>
        <Button
          onClick={handleSearchWorker}
          sx={{
            fontSize: "18px",
            borderRadius: "32px",
            bgcolor: "primary.main",
            color: "white",
            ml: 2,
            minWidth: "0",
            // "&:hover": { bgcolor: "#FF385C" },
            // padding: "6px 15px",
            "@media (max-width: 600px)": {
              fontSize: "14px",
              //   padding: "5px 12px",
              ml: 1,
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 30 }} />
        </Button>
      </Box>
    </Paper>
  );
};

export default SearchBar;
